import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export const WAITER_STORAGE_KEY = 'waiter';

// Spec §1.7 — generalises the old table-only `lockedFromTableToken`: a lock
// (manual 🔒 or idle timeout) can now fire from either an open TABLE
// (`/table/:tableToken`, SC-759 flow, kept unchanged) or an open TICKET
// (`/serveur/ticket/:sessionUuid`, the new notebook). A successful re-PIN
// returns the waiter to whichever one they were serving instead of the bare
// notes list. `null`/absent when the lock happened from the list itself.
export type LockedFrom = { kind: 'table'; tableToken: string } | { kind: 'ticket'; sessionUuid: string };

export interface WaiterAuth {
  restaurantUuid: string;
  token: string;
  deviceUuid: string;
  waiterName: string;
  waiterNames: string[];
  idleLockSeconds: number;
  expiresAt: string;
  uiLocked: boolean;
  // A fresh setWaiterAuth (any unlock) always drops it — single-use.
  lockedFrom?: LockedFrom | null;
  /**
   * SC-761 — the roster name this device is switching TO, picked in the
   * identity sheet of the notes list BEFORE the re-PIN. `startNameChange`
   * writes it, the PinPad re-unlocks with it, and `setWaiterAuth` drops it
   * (it is not part of the unlock payload) — single-use, like `lockedFrom`.
   * Null/absent = the lock screen is a plain re-lock, not a switch.
   */
  pendingWaiterName?: string | null;
}

/**
 * Spec §1.7 — "Staff-ness" for the ticket screens (SC-692): the fact that the
 * currently open ticket belongs to the waiter surface is posed by the
 * `/serveur/ticket/:sessionUuid` route into THIS store field, never deduced
 * from the waiter token living in localStorage (which survives a tab a guest
 * later reuses to scan a QR — see `waiterSurface.ts`). F1's NotesListPage /
 * F2's TicketPage set this on navigation; F0 only declares the shape + the
 * actions so those slices have something to dispatch into.
 */
export interface WaiterActiveTicket {
  sessionUuid: string;
  kind: 'dine_in' | 'takeaway';
  /** Table number (dine_in) or free-text Emporter label (takeaway) — the
   *  ticket header renders `N° {{label}}` or the bare label per kind. */
  label: string;
  covers: number;
  openedAt: string;
  guestToken: string;
  /** SC-976 — design §5.3: whether `label` is a tablée (« 5+6 ») rather than
   *  a single table number — the header's ⛓ mark reads this directly rather
   *  than re-parsing the label string. */
  isMerged?: boolean;
}

// Payload for a (re-)unlock: uiLocked is never accepted from the caller — a
// fresh or re- unlock always lands with the lock screen cleared.
export type SetWaiterAuthPayload = Omit<WaiterAuth, 'uiLocked'>;

interface WaiterState {
  auth: WaiterAuth | null;
  // Not persisted (unlike `auth`): a fresh mount always re-derives it from
  // the route (self-sufficient URL, spec §1.7) rather than trusting a stale
  // in-memory value across a reload.
  activeTicket: WaiterActiveTicket | null;
}

const initialState: WaiterState = {
  auth: null,
  activeTicket: null,
};

function storageKey(restaurantUuid: string): string {
  return `${WAITER_STORAGE_KEY}:${restaurantUuid}`;
}

function persistToStorage(auth: WaiterAuth): void {
  try {
    localStorage.setItem(storageKey(auth.restaurantUuid), JSON.stringify(auth));
  } catch {
    // localStorage unavailable (e.g. private mode with storage blocked)
  }
}

function removeFromStorage(restaurantUuid: string): void {
  try {
    localStorage.removeItem(storageKey(restaurantUuid));
  } catch {
    // localStorage unavailable
  }
}

function loadFromStorage(restaurantUuid: string): WaiterAuth | null {
  try {
    const raw = localStorage.getItem(storageKey(restaurantUuid));
    if (!raw) return null;
    const parsed: WaiterAuth = JSON.parse(raw);
    // Discard an expired token so the shell falls back to the PIN pad instead
    // of surfacing a dead session.
    if (parsed.expiresAt && new Date(parsed.expiresAt) < new Date()) {
      removeFromStorage(restaurantUuid);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/**
 * SC-692 — lecture SEULE de l'auth serveur persistee, sans passer par le store.
 * Le parcours client (TableLanding) a besoin de savoir « cet appareil est-il
 * aussi celui d'un serveur ? » pour une redirection, mais publier cette auth
 * dans redux reveille les outils de salle sur l'ecran du client.
 */
export function readPersistedWaiterAuth(restaurantUuid: string): WaiterAuth | null {
  return loadFromStorage(restaurantUuid);
}

const waiterSlice = createSlice({
  name: 'waiter',
  initialState,
  reducers: {
    setWaiterAuth(state, action: PayloadAction<SetWaiterAuthPayload>) {
      const auth: WaiterAuth = { ...action.payload, uiLocked: false };
      state.auth = auth;
      persistToStorage(auth);
    },
    clearWaiterAuth(state, action: PayloadAction<string>) {
      state.auth = null;
      removeFromStorage(action.payload);
    },
    // SC-799 — renouvellement glissant : SEULS le jeton et son echeance
    // bougent. Le jeton serveur expire au bout de 12 h fixes cote serveur, et
    // rien ne repoussait cette echeance : un telephone en service quotidien
    // repassait par le pave PIN chaque matin, meme avec « ne jamais
    // verrouiller » (qui ne pilote que le minuteur d'inactivite). Un
    // renouvellement arrive en fond, y compris derriere l'ecran de
    // verrouillage, donc `uiLocked` / `waiterName` / `lockedFrom` /
    // `pendingWaiterName` doivent survivre intacts — sinon il serait visible.
    renewWaiterToken(
      state,
      action: PayloadAction<{ restaurantUuid: string; token: string; expiresAt: string }>,
    ) {
      // Une reponse en vol pour un AUTRE restaurant (ou apres une
      // deconnexion) ne doit jamais ecraser l'auth courante.
      if (!state.auth || state.auth.restaurantUuid !== action.payload.restaurantUuid) return;
      state.auth.token = action.payload.token;
      state.auth.expiresAt = action.payload.expiresAt;
      persistToStorage(state.auth);
    },
    setUiLocked(state, action: PayloadAction<boolean>) {
      if (state.auth) {
        state.auth.uiLocked = action.payload;
        persistToStorage(state.auth);
      }
    },
    setLockedFrom(state, action: PayloadAction<LockedFrom>) {
      if (state.auth) {
        state.auth.lockedFrom = action.payload;
        persistToStorage(state.auth);
      }
    },
    setWaiterName(state, action: PayloadAction<string>) {
      if (state.auth) {
        state.auth.waiterName = action.payload;
        persistToStorage(state.auth);
      }
    },
    // SC-572: "Changer d'utilisateur" — a wrong pick used to be permanent (the
    // name is persisted, so even quitting and reopening restored it). Locking
    // sends the device back through the PinPad, which is the only path that
    // can rebind a name: `unlock/` is the sole endpoint carrying waiter_name,
    // so a switch always costs a re-PIN (spec §2.3) — that is also what stops
    // anyone grabbing the handheld and serving under someone else's name.
    //
    // SC-761: with a target name (picked in the notes-list identity sheet
    // before the PIN is asked for), the current name is KEPT until that
    // unlock actually succeeds — so cancelling the PIN screen leaves the
    // waiter as who they were instead of stranding the device nameless.
    // Without one (legacy caller), the old behaviour stands: drop the name
    // and let the shell land on the name picker after the re-PIN.
    startNameChange(state, action: PayloadAction<string | undefined>) {
      if (state.auth) {
        const target = action.payload;
        state.auth.pendingWaiterName = target ?? null;
        if (!target) state.auth.waiterName = '';
        state.auth.uiLocked = true;
        persistToStorage(state.auth);
      }
    },
    // The waiter backed out of the switch on the PIN screen: the device stays
    // locked (it was handed over on purpose) but re-unlocks under its own
    // name again.
    cancelNameChange(state) {
      if (state.auth) {
        state.auth.pendingWaiterName = null;
        persistToStorage(state.auth);
      }
    },
    rehydrate(state, action: PayloadAction<string>) {
      state.auth = loadFromStorage(action.payload);
    },
    setTicket(state, action: PayloadAction<WaiterActiveTicket>) {
      state.activeTicket = action.payload;
    },
    clearTicket(state) {
      state.activeTicket = null;
    },
  },
});

export const {
  setWaiterAuth,
  clearWaiterAuth,
  renewWaiterToken,
  setUiLocked,
  setLockedFrom,
  setWaiterName,
  startNameChange,
  cancelNameChange,
  rehydrate,
  setTicket,
  clearTicket,
} = waiterSlice.actions;

export const waiterReducer = waiterSlice.reducer;
export default waiterSlice.reducer;

// Selectors
type StateWithWaiter = { waiter: WaiterState };

export const selectWaiterAuth = (state: StateWithWaiter): WaiterAuth | null => state.waiter.auth;

export const selectWaiterToken = (state: StateWithWaiter): string | null =>
  state.waiter.auth?.token ?? null;

export const selectIsWaiterUiLocked = (state: StateWithWaiter): boolean =>
  state.waiter.auth?.uiLocked ?? false;

export const selectActiveTicket = (state: StateWithWaiter): WaiterActiveTicket | null =>
  state.waiter.activeTicket;
